import { useEffect, useRef } from "react";
import { Headphones, Mic, MicOff, PhoneOff, Video, MonitorUp } from "lucide-react";
import { toast } from "sonner";
import { Avatar } from "./Avatar";
import { useChat } from "@/lib/chat";

const RemoteAudio = ({ stream }) => {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.srcObject = stream; }, [stream]);
  return <audio ref={ref} autoPlay playsInline />;
};

export const HuddleWindow = () => {
  const { huddles, myHuddleId, huddleState, leaveHuddle, toggleMute, meId, conversations } = useChat();
  const h = huddles.find((x) => x.id === myHuddleId);
  if (!h) return null;
  const conv = conversations.find((c) => c.id === h.conversation_id);
  const title = conv ? (conv.type === "channel" ? `#${conv.name}` : conv.display_name) : "Huddle";
  return (
    <div className="tc-huddle-window tc-fade-in" data-testid="huddle-window">
      {Object.entries(huddleState.streams).map(([uid, s]) => <RemoteAudio key={uid} stream={s} />)}
      <div className="tc-huddle-window__title"><Headphones size={14} /> <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</span><span className="live">Live</span></div>
      <div className="tc-huddle-grid">
        {(h.participants_info || []).map((u) => (
          <div key={u.id} className={`tc-huddle-tile${huddleState.speaking[u.id] ? " is-speaking" : ""}`} data-testid={`huddle-participant-${u.id}`}>
            <Avatar user={u} size={32} radius={8} />
            <span className="nm">{u.id === meId ? "You" : u.display_name.split(" ")[0]}</span>
            {u.id === meId && huddleState.muted && <span className="muted"><MicOff /></span>}
          </div>
        ))}
      </div>
      {huddleState.micError && <div className="tc-huddle-window__err" style={{ fontSize: 11, color: "#f2c744", marginBottom: 8 }}>Mic unavailable — you can listen only.</div>}
      <div className="tc-huddle-controls">
        <button className={`tc-huddle-ctl${huddleState.muted ? " is-off" : ""}`} onClick={toggleMute} title={huddleState.muted ? "Unmute" : "Mute"} data-testid="huddle-mute-button">{huddleState.muted ? <MicOff /> : <Mic />}</button>
        <button className="tc-huddle-ctl" onClick={() => toast("Video in huddles is coming soon")} title="Turn on video" data-testid="huddle-video-button"><Video /></button>
        <button className="tc-huddle-ctl" onClick={() => toast("Screen sharing is coming soon")} title="Share screen" data-testid="huddle-share-button"><MonitorUp /></button>
        <button className="tc-huddle-ctl is-leave" onClick={leaveHuddle} data-testid="huddle-leave-button"><PhoneOff /> Leave</button>
      </div>
    </div>
  );
};

export const HuddleBanner = ({ conversationId }) => {
  const { huddles, myHuddleId, joinHuddle } = useChat();
  const h = huddles.find((x) => x.conversation_id === conversationId);
  if (!h || h.id === myHuddleId) return null;
  const starter = h.participants_info?.find((u) => u.id === h.started_by) || h.participants_info?.[0];
  return (
    <div className="tc-huddle-banner tc-fade-in" data-testid="huddle-banner">
      <span className="avatars">{(h.participants_info || []).slice(0, 4).map((u) => <Avatar key={u.id} user={u} size={24} radius={5} />)}</span>
      <span className="txt"><b>{starter?.display_name}</b> {h.participants.length > 1 ? `and ${h.participants.length - 1} other${h.participants.length > 2 ? "s are" : " is"}` : "is"} in a huddle</span>
      <button className="tc-btn tc-btn--primary sm" onClick={() => joinHuddle(conversationId)} data-testid="huddle-banner-join"><Headphones size={14} /> Join</button>
    </div>
  );
};
